import { useState, useEffect } from 'react';
import Button from 'components/base/Button';
import ReactSelect from 'components/base/ReactSelect';
import { Form, Row, Col, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import LoadingOverlay from 'components/loading-overlay/LoadingOverlay';
import PageBreadcrumb from 'components/common/PageBreadcrumb';
import { createRepairCategory, getAllRepairOptions } from 'api/api';

const breadcrumbItems = [
  {
    label: 'Repair Categories',
    url: '/repair/repairCategories'
  },
  {
    label: 'Add',
    active: true
  }
];

function AddRepairCategory() {
  const navigate = useNavigate();
  const [error, setError] = useState([]);
  const [repairOptions, setRepairOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    price: '',
    warranty_price: '',
    warranty_months: '',
    repair_options: []
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = () => {
    setError([]);
    setLoading(true);

    getAllRepairOptions()
      .then(response => {
        const options = response.data.data.map(option => ({
          id: option.id,
          name: option.name
        }));
        setRepairOptions(options);
      })
      .catch(err => {
        try {
          setError(err.response.data.errors);
        } catch (ex) {
          setError(['Please try again later...']);
        }
      });

    setTimeout(function () {
      setLoading(false);
    }, 1000);
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = e => {
    e.preventDefault();
    setError([]);
    setLoading(true);

    const formattedFormData = {
      ...formData,
      price: parseFloat(formData.price),
      warranty_price: parseFloat(formData.warranty_price),
      warranty_months: parseInt(formData.warranty_months, 10)
    };

    createRepairCategory(formattedFormData)
      .then(response => {
        if (response.status === 200) navigate('/repair/repairCategories');
      })
      .catch(err => {
        try {
          setError(err.response.data.errors);
        } catch (error) {
          setError(['Please try again later...']);
        }
      });

    setTimeout(function () {
      setLoading(false);
    }, 1000);
  };

  return (
    <div>
      <PageBreadcrumb items={breadcrumbItems} />
      <div className="mb-9">
        <div className="d-flex flex-wrap gap-3 flex-between-end mb-5">
          <div>
            <h2 className="mb-2">Add Repair Category</h2>
            <h5 className="text-700 fw-semi-bold">Add a new repair category</h5>
          </div>
        </div>
        <div>
          {Object.entries(error).length > 0 &&
            error.map((e, index) => (
              <Alert variant="soft-danger" key={index}>
                {e}
              </Alert>
            ))}
        </div>

        {loading && <LoadingOverlay />}
        <Row className="justify-content-left">
          <Col md={8}>
            <Form onSubmit={handleSubmit}>
              <Row>
                <Col md={8}>
                  <Form.Group className="mb-3" controlId="formName">
                    <Form.Label> Category Name</Form.Label>
                    <Form.Control
                      type="text"
                      name="title"
                      value={formData.title || ''}
                      onChange={handleChange}
                      placeholder="Enter category name"
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={8}>
                  <Form.Group className="mb-3" controlId="formPrice">
                    <Form.Label>Price</Form.Label>
                    <Form.Control
                      type="number"
                      name="price"
                      value={formData.price}
                      onChange={handleChange}
                      placeholder="Enter price"
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={8}>
                  <Form.Group className="mb-3" controlId="formWarrantyPrice">
                    <Form.Label>Warranty Price</Form.Label>
                    <Form.Control
                      type="number"
                      name="warranty_price"
                      value={formData.warranty_price}
                      onChange={handleChange}
                      placeholder="Enter warranty price"
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={8}>
                  <Form.Group className="mb-3" controlId="formWarrantyMonth">
                    <Form.Label>Warranty Months</Form.Label>
                    <Form.Control
                      type="number"
                      name="warranty_months"
                      value={formData.warranty_months}
                      onChange={handleChange}
                      placeholder="Enter warranty months"
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={8}>
                  <Form.Group className="mb-3" controlId="formRepairOptions">
                    <Form.Label>Repair Options</Form.Label>
                    <ReactSelect
                      isMulti
                      options={repairOptions.map(option => ({
                        value: option.id,
                        label: option.name
                      }))}
                      value={(formData.repair_options || []).map(optionId => {
                        const selectedOption = repairOptions.find(
                          option => option.id === optionId
                        );
                        return selectedOption
                          ? {
                              value: selectedOption.id,
                              label: selectedOption.name
                            }
                          : null;
                      })}
                      onChange={selectedOptions => {
                        const selectedValues = selectedOptions.map(
                          option => option.value
                        );
                        setFormData({
                          ...formData,
                          repair_options: selectedValues
                        });
                      }}
                      placeholder="Select repair options"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={3} className="d-flex align-items-end">
                  <Button variant="primary" type="submit" className="w-100">
                    Submit
                  </Button>
                </Col>
                <Col md={3} className="d-flex align-items-end">
                  <Button
                    className="w-100"
                    variant="phoenix-secondary"
                    type="button"
                    onClick={() => navigate('/repair/repairCategories')}
                  >
                    Discard
                  </Button>
                </Col>
              </Row>
            </Form>
          </Col>
        </Row>
      </div>
    </div>
  );
}

export default AddRepairCategory;
